import { isValidUrlCountryLocale } from '@xarvio/frontend-core-platform/lib/country/urlLocaleValidation';
import { CountryUtils } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';
import type { DecodedUserToken } from '@xarvio/frontend-core-state/lib/auth/types';
import { isTokenExpired } from '@xarvio/frontend-core-state/lib/auth/utils';
import {
  defaultCountry,
  defaultLocale,
} from '@xarvio/frontend-core-state/lib/intl/constants';

/**
 * Pick the redirect target for an invalid /:country/:locale/ URL prefix:
 * the user's default from the DF token when it yields a valid combination
 * (guests and malformed tokens fall back to the app defaults, which also
 * protects against a redirect loop). Returns null when the URL prefix is
 * already valid.
 */
export const resolveLocaleRedirectTarget = (
  country: string,
  locale: string,
  decodedToken: DecodedUserToken,
): { country: string; locale: string } | null => {
  if (isValidUrlCountryLocale(country, locale)) {
    return null;
  }

  const tokenLocale = `${decodedToken.language}_${decodedToken.country}`;

  if (
    !isTokenExpired(decodedToken) &&
    isValidUrlCountryLocale(decodedToken.country, tokenLocale)
  ) {
    return { country: decodedToken.country, locale: tokenLocale };
  }

  return { country: defaultCountry, locale: defaultLocale };
};

interface GetUnprefixedRouteRedirectTargetProps {
  countryCode: string | null;
  defaultRedirectPath: string;
  languageCode: string | null;
  pathname: string;
  search: string;
  storeCountry: string;
  storeLocale: string;
}

export const getUnprefixedRouteRedirectTarget = ({
  countryCode,
  defaultRedirectPath,
  languageCode,
  pathname,
  search,
  storeCountry,
  storeLocale,
}: GetUnprefixedRouteRedirectTargetProps): string => {
  if (
    countryCode &&
    languageCode &&
    CountryUtils.isSupportedCountry(countryCode)
  ) {
    return `/${countryCode}/${languageCode}${pathname}${search}`;
  }

  if (
    storeCountry &&
    storeLocale &&
    CountryUtils.isSupportedCountry(storeCountry)
  ) {
    return `/${storeCountry}/${storeLocale}${pathname}${search}`;
  }

  return defaultRedirectPath;
};
